import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import Table from "../../components/Table/Table";
import classes from "./Members.module.css";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import CommonButton from "../../components/ui/CommonButton/CommonButton";
import AlertDialog from "../../components/ui/AlertDialog/AlertDialog";
import InputField from "../../components/ui/InputField/InputField";
import axios from "axios";
import { env } from "../../constants/env"; // Make sure you have your API base URL here
import { toast } from "react-toastify";

const roles = ["Admin", "Fleet manager", "Customer success"];
const statusOptions = ["Active", "Inactive"];

const initialFormData = {
  name: "",
  email: "",
  role: "",
  status: "Active",
  password: "", // new field
};

const Members = () => {
  const [members, setMembers] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const limit = 10; // items per page

  const fetchMembers = async (page = 1, search = "") => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${env.apiUrl}/api/users/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { page, limit, search },
      });

      // Inside your component, before setting members:
      const formattedMembers = response.data.users.map((user) => ({
        ...user,
        joinDate: new Date(
          user.joinDate || user.createdAt
        ).toLocaleDateString(),
      }));

      setMembers(formattedMembers);
      setPageCount(response.data.pages);
      setCurrentPage(response.data.page);
    } catch (error) {
      console.error("Failed to fetch members:", error);
    }
  };

  useEffect(() => {
    fetchMembers(currentPage, searchTerm);
  }, [currentPage, searchTerm]);

  const columns = [
    // { key: "id", label: "ID" },
    { key: "name", label: "NAME" },
    { key: "email", label: "EMAIL" },
    { key: "joinDate", label: "JOIN DATE" },
    { key: "role", label: "ROLE" },
    { key: "status", label: "STATUS" },
  ];

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
  };

  const handleChange = (e) => {
    const newData = { [e.target.name]: e.target.value };
    setFormData((prev) => ({ ...prev, ...newData }));
  };

  const handleSaveMember = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      if (dialogContent === "add") {
        const response = await axios.post(
          `${env.apiUrl}/api/users/members`,
          {
            name: formData.name,
            email: formData.email,
            role: formData.role,
            status: formData.status,
            password: formData.password,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // ✅ Format new user with joinDate
        const formattedUser = {
          ...response.data.user,
          joinDate: new Date(
            response.data.user.joinDate || response.data.user.createdAt
          ).toLocaleDateString(),
        };

        setMembers((prev) => [...prev, formattedUser]);
      }

      if (dialogContent === "edit" && selectedMember !== null) {
        const memberId =
          members[selectedMember]?.id || members[selectedMember]?._id;

        const response = await axios.put(
          `${env.apiUrl}/api/users/members/${memberId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // ✅ Format updated user with joinDate
        const formattedUser = {
          ...response.data.user,
          joinDate: new Date(
            response.data.user.joinDate || response.data.user.createdAt
          ).toLocaleDateString(),
        };

        // Update the list
        setMembers((prev) =>
          prev.map((m, i) => (i === selectedMember ? formattedUser : m))
        );
      }

      // Reset form and close dialog
      setFormData(initialFormData);
      setShowDialog(false);
      toast.success("Successfully saved member");
    } catch (err) {
      console.error("Failed to save member:", err);
      const message = err.response?.data?.message || "Failed to save member";
      toast.error(message);
    }
  };

  const handleDeleteMember = async () => {
    if (selectedMember === null) return;

    try {
      const token = localStorage.getItem("adminToken");
      const memberId =
        members[selectedMember]?.id || members[selectedMember]?._id;

      await axios.delete(`${env.apiUrl}/api/users/members/${memberId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setMembers((prev) => prev.filter((_, i) => i !== selectedMember));
      setShowTableDialog(false);
      toast.success("Member deleted successfully");
    } catch (err) {
      console.error("Failed to delete member:", err);
      toast.error("Failed to delete member");
    }
  };

  return (
    <div className={classes.wrapper}>
      <div className={`d-flex justify-content-between align-items-center mb-3 ${classes.headerRow}`}>
        <InputField
          className="mb-3"
          search
          placeholder="Search members"
          inputContainerClass={classes.inputContainer}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page on search
          }}
        />
        <CommonButton
          title="Add new member"
          onClick={() => {
            setShowDialog(true);
            setDialogContent("add");
          }}
        />
      </div>

      <Table
        dataType={"Members"}
        data={members}
        columns={columns}
        showDialog={showTableDialog}
        setShowDialog={setShowTableDialog}
        editMember={() => {
          if (selectedMember !== null) {
            const member = members[selectedMember];
            setFormData({
              name: member.name || "",
              email: member.email || "",
              role: member.role || "",
              status: member.status || "Active",
              password: "", // leave blank, only for setting a new password
            });
            setShowDialog(true);
            setDialogContent("edit");
          }
        }}
        deleteMember={handleDeleteMember}
        clickedIndex={(value) => setSelectedMember(value)}
      />

      <AlertDialog
        isOpen={showDialog}
        toggleSidebar={() => { setFormData(initialFormData); setShowDialog(false); }}
        onClose={() => {
          setFormData(initialFormData);
          setShowDialog(false);
        }}
        titleText={dialogContent === "edit" ? "Edit member" : "Add new member"}
        showCloseIcon
        style={{
          left: `calc(50vw - 250px)`,
          width: 500,
        }}
        showToSection={
          dialogContent !== "delete" && dialogContent !== "suspend"
        }
      >
        <div className={`d-flex flex-column gap-3 ${classes.containerBox}`}>
          <InputField
            labelText="Name"
            placeholder="Member name"
            value={formData.name}
            name="name"
            onChange={handleChange}
          />
          <InputField
            labelText="Email"
            placeholder="Member email"
            value={formData.email}
            name="email"
            onChange={handleChange}
          />
          <InputField
            labelText="Role"
            options={roles}
            value={formData.role}
            name="role"
            onChange={handleChange}
            showSearch={false}
          />
          <InputField
            labelText="Password"
            placeholder="Enter password"
            type="password"
            value={formData.password}
            name="password"
            onChange={handleChange}
          />
          <InputField
            labelText="Status"
            options={statusOptions}
            value={formData.status}
            name="status"
            onChange={handleChange}
            showSearch={false}
          />
          <CommonButton
            title={dialogContent === "edit" ? "Save changes" : "Add member"}
            disabled={
              formData.name === "" ||
              formData.email === "" ||
              formData.role === "" ||
              (dialogContent === "add" && formData.password === "") // require password on add
            }
            onClick={handleSaveMember}
          />
        </div>
      </AlertDialog>

      <div className="mt-4">
        <ReactPaginate
          previousLabel={
            <FaChevronLeft
              color={
                currentPage === 1
                  ? "var(--border-default)"
                  : "var(--text-secondary-color)"
              }
            />
          }
          nextLabel={
            <FaChevronRight
              color={
                currentPage === pageCount
                  ? "var(--border-default)"
                  : "var(--text-secondary-color)"
              }
            />
          }
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={1}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          subContainerClassName={"pages pagination"}
          previousLinkClassName={`page-link ${
            currentPage === 1 ? "disabled" : ""
          }`}
          nextLinkClassName={`page-link ${
            currentPage === pageCount ? "disabled" : ""
          }`}
          activeClassName={"active"}
        />
      </div>
    </div>
  );
};

export default Members;
